import { useEffect, useState } from "react";
import { serviceLabel } from "@/lib/serviceLabel";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { CreditCard, CheckCircle2, ArrowLeft, Receipt } from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import ZelleInstructions from "@/components/site/ZelleInstructions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (v) => `$${Number(v || 0).toFixed(2)}`;

function InvoiceTable({ invoice, money: m }) {
  return (
    <Card className="p-5 border-2 border-zinc-200" data-testid="pay-invoice">
      <div className="flex items-center gap-2 mb-3"><Receipt className="w-4 h-4 text-[#0C2E2B]" /><h3 className="font-display font-black text-[#0C2E2B]">Invoice · v{invoice.version}</h3></div>
      <table className="w-full text-sm">
        <tbody>
          {invoice.items.map((i, n) => (
            <tr key={n} className="border-b border-zinc-100"><td className="py-1.5">{i.label}</td><td className="py-1.5 text-right font-semibold">{money(i.amount)}</td></tr>
          ))}
          <tr><td className="pt-2 text-zinc-500 text-xs">Subtotal</td><td className="pt-2 text-right text-xs">{money(invoice.subtotal)}</td></tr>
          {invoice.discount > 0 && <tr><td className="text-zinc-500 text-xs">Discount</td><td className="text-right text-xs text-emerald-700">−{money(invoice.discount)}</td></tr>}
          <tr><td className="font-bold">Total</td><td className="text-right font-bold">{money(invoice.total)}</td></tr>
          {m.paid > 0 && <tr><td className="text-zinc-500 text-xs">Paid so far</td><td className="text-right text-xs text-emerald-700">−{money(m.paid)}</td></tr>}
        </tbody>
      </table>
      {invoice.notes && <p className="text-xs text-zinc-600 mt-3 bg-zinc-50 border-l-4 border-[#0C2E2B] px-3 py-2">{invoice.notes}</p>}
    </Card>
  );
}

export default function PayPage() {
  const { ref } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/pay/${ref}`).then(({ data }) => setInfo(data)).catch((e) => setError(e?.response?.status === 404 ? "We couldn't find that booking." : "Something went wrong loading your booking."));
  }, [ref]);

  const payCard = async (kind) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/pay/${ref}/checkout`, { kind, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start card checkout");
      setBusy(false);
    }
  };

  const dueKind = info?.deposit_due ? "deposit" : (info?.money?.balance_due > 0 ? "balance" : null);
  const dueAmount = dueKind === "deposit" ? info.deposit_amount : dueKind === "balance" ? info.money.balance_due : 0;

  return (
    <div className="min-h-screen bg-[#FAF8F5]" data-testid="pay-page">
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 pt-28 pb-20">
        <Link to="/" className="inline-flex items-center gap-1 text-sm font-bold text-[#0C2E2B] hover:underline"><ArrowLeft className="w-4 h-4" /> Home</Link>

        {error && <Card className="mt-6 p-8 text-center border-2 border-red-200" data-testid="pay-error"><p className="font-bold text-red-700">{error}</p><p className="text-sm text-zinc-500 mt-1">Check the link in your email or reply to it and we'll help.</p></Card>}
        {!info && !error && <p className="mt-8 text-sm text-zinc-500">Loading your booking…</p>}

        {info && (
          <div className="mt-6 space-y-5">
            <div>
              <div className="font-tactical text-xs uppercase tracking-[0.25em] text-[#0C2E2B]/60">Booking {info.ref_id}</div>
              <h1 className="font-display font-black text-4xl sm:text-5xl text-[#0C2E2B] mt-1">Hi {info.first_name}, {dueKind ? "here's what's due" : "you're all set"}</h1>
              <p className="text-zinc-600 mt-2 text-sm md:text-base">
                {info.kind === "order"
                  ? `Merch order · ${(info.items || []).map((i) => `${i.quantity}× ${i.title}`).join(", ")} · ships to ${info.address}`
                  : `${serviceLabel(info.service_type)} · ${info.preferred_date} · ${info.preferred_window} · ${info.address}, ${info.zip_code}`}
              </p>
            </div>

            <Card className="p-6 border-2 border-[#0C2E2B] bg-[#0C2E2B] text-white" data-testid="pay-due-card">
              {dueKind ? (
                <>
                  <div className="text-[11px] uppercase tracking-[0.2em] text-white/70 font-bold">{dueKind === "deposit" ? (info.kind === "order" ? "Order total" : (info.pay_in_full ? "Total due for your delivery & assembly" : "Deposit to reserve your slot")) : "Balance due"}</div>
                  <div className="font-display font-black text-5xl text-[#FFDF00] mt-1" data-testid="pay-due-amount">{money(dueAmount)}</div>
                  {dueKind === "balance" && info.money?.promo_discount > 0 && <p data-testid="pay-promo-line" className="text-xs text-white/80 mt-1">Job price {money(info.money.gross)} − promo {info.money.promo_code} {money(info.money.promo_discount)} = {money(info.money.total)}{info.money.paid > 0 ? ` · paid ${money(info.money.paid)}` : ""}</p>}
                  {dueKind === "deposit" && info.kind !== "order" && <p className="text-xs text-white/70 mt-2">Applied to your final bill. {info.cancellation_policy}</p>}
                  <Button data-testid="pay-card-btn" disabled={busy} onClick={() => payCard(dueKind)} className="mt-5 w-full sm:w-auto bg-[#FFDF00] text-[#0C2E2B] hover:bg-[#E5C900] font-black rounded-md px-8 py-6 text-base">
                    <CreditCard className="w-5 h-5 mr-2" /> {busy ? "Opening secure checkout…" : `Pay ${money(dueAmount)} by card`}
                  </Button>
                  <p className="text-[11px] text-white/60 mt-2">Secure checkout by Stripe. Or use Zelle below — no fees.</p>
                </>
              ) : (
                <div className="flex items-center gap-3" data-testid="pay-nothing-due">
                  <CheckCircle2 className="w-8 h-8 text-[#FFDF00]" />
                  <div>
                    <div className="font-display font-black text-2xl">Nothing due right now</div>
                    <p className="text-xs text-white/70">{info.money?.paid > 0 ? `Paid ${money(info.money.paid)} so far. ` : ""}We'll email you if a balance comes up after the job.</p>
                  </div>
                </div>
              )}
            </Card>

            {dueKind && (
              <ZelleInstructions
                zelle={{ ...info.zelle, amount: dueAmount }}
                refId={info.ref_id}
                purpose={info.kind === "order" ? "order" : "booking"}
                cancellationPolicy={dueKind === "deposit" && info.kind !== "order" ? info.cancellation_policy : null}
              />
            )}

            {info.invoice && <InvoiceTable invoice={info.invoice} money={info.money} />}

            {info.payments?.length > 0 && (
              <Card className="p-4 border-2 border-zinc-200 text-sm" data-testid="pay-history">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-2">Payments received</div>
                {info.payments.map((p, n) => (
                  <div key={n} className="flex justify-between py-1 border-b border-zinc-100 last:border-0"><span>{p.label} · {p.paid_via}</span><span className="font-semibold text-emerald-700">{money(p.amount)}</span></div>
                ))}
              </Card>
            )}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
